import { FsMatchTrack } from "db/model/match/Track/FsMatchTrack";
import { MatchmakingTrack } from "model/Match/Set/Matchmaking/Track/MatchmakingTrack";
import { PointsDistribution } from "model/Match/Set/PointsDistribution";
import { nonUndefined } from "Utility";
import { mapMatchmakingRounds } from "./FsMatchmakingRound";
import { getDefinedRosterIds, Roster } from "model/Roster/Roster";
import { MatchmakingRoundResult } from "model/Match/Set/Matchmaking/Track/MatchmakingRoundResult";
import { Scores } from "model/Scores";
import { mapMatchTrackBase } from "../../Track/FsMatchTrack";
import { mapTrackRounds } from "../../Track/FsTrackRound";
import { calculateFinalPositions } from "model/Positions";
import { RosterScope } from "model/Roster/RosterScope";

export const mapMatchmakingTracks = (
  fsModels: FsMatchTrack[],
  rosters: Roster[],
  rosterScope: RosterScope,
  pointsDist: PointsDistribution,
  roundsLimit: number | undefined,
  trackWinReq: number,
  overrideComplete: boolean | undefined,
  isSms: boolean
): MatchmakingTrack[] => {
  return nonUndefined(
    fsModels.map((t, i) =>
      mapMatchmakingTrack(
        t,
        rosters,
        rosterScope,
        isSms && t.trackId === "563"
          ? {
              dnfPoints: 0,
              distribution:
                i === 0 ? [2, 1] : i === 1 ? [4, 3, 2, 1] : [6, 5, 4, 3, 2, 1],
            }
          : pointsDist,
        roundsLimit,
        trackWinReq,
        overrideComplete
      )
    )
  );
};

const mapMatchmakingTrack = (
  fsModel: FsMatchTrack,
  rosters: Roster[],
  rosterScope: RosterScope,
  pointsDist: PointsDistribution,
  roundsLimit: number | undefined,
  winReq: number,
  overrideComplete: boolean | undefined
): MatchmakingTrack | undefined => {
  const baseRounds = mapTrackRounds(nonUndefined(fsModel.rounds ?? []));
  const rounds = mapMatchmakingRounds(
    baseRounds,
    rosters,
    rosterScope,
    pointsDist
  );
  const scores = calculateTrackScores(
    rounds.flatMap((r) => r.results),
    fsModel.scoreOverrides,
    getDefinedRosterIds(rosters)
  );

  const numRoundsPlayed = rounds.filter((r) => !!r.results.length).length;
  const isComplete =
    overrideComplete ||
    fsModel.overrideComplete ||
    calculateIsComplete(roundsLimit, numRoundsPlayed, scores, winReq);

  return {
    ...mapMatchTrackBase(fsModel),
    finalPositions: calculateFinalPositions(isComplete, scores, compareScores),
    isComplete,
    rounds,
    scores,
  };
};

const calculateTrackScores = (
  results: MatchmakingRoundResult[],
  scoreOverrides: Scores | undefined,
  rosterIds: string[]
) => {
  const scores: Scores = {};
  for (var i = 0; i < rosterIds.length; i++) {
    const rosterId = rosterIds[i];
    const scoreOverride = !!scoreOverrides
      ? scoreOverrides[rosterId]
      : undefined;
    if (scoreOverride !== undefined) {
      scores[rosterId] = scoreOverride;
    } else {
      const teamResults = results.filter((r) => r.rosterId === rosterId);
      scores[rosterId] = teamResults.reduce((a, b) => a + b.trackPoints, 0);
    }
  }

  return scores;
};

const calculateIsComplete = (
  roundsLimit: number | undefined,
  numRoundsPlayed: number,
  scores: Scores,
  winReq: number
) => {
  if (!!roundsLimit && numRoundsPlayed >= roundsLimit) {
    return true;
  }

  return Math.max(...Object.values(scores), 0) >= winReq;
};

const compareScores = (scores: Scores, a: string, b: string) => {
  return scores[b] - scores[a];
};
